import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Flower2, Sparkles, Gift, Ticket, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API } from '@/App';

function RegisterFreePage({ setIsAuthenticated }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);

  const signupType = searchParams.get('type'); // 'founding' or 'invite'
  const inviteCode = searchParams.get('code');

  const isFoundingMember = signupType === 'founding';
  const isInviteCode = signupType === 'invite' && inviteCode;

  useEffect(() => {
    const validateAccess = async () => {
      // Check if user is already logged in
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await axios.get(`${API}/user/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.subscription_active) {
            toast.info('You already have an active subscription!');
            navigate('/dashboard');
            return;
          }
        } catch (error) {
          localStorage.clear();
        }
      }

      if (isFoundingMember) {
        // Check founding member availability
        try {
          const response = await axios.get(`${API}/promo/founding-member-status`);
          if (!response.data.is_available) {
            toast.error('Sorry, all founding member spots have been claimed!');
            navigate('/pricing');
            return;
          }
          setIsValid(true);
        } catch (error) {
          toast.error('Failed to check founding member availability');
          navigate('/pricing');
          return;
        }
      } else if (isInviteCode) {
        // Validate invite code
        try {
          const response = await axios.post(`${API}/promo/validate-invite-code`, {
            code: inviteCode
          });
          if (!response.data.valid) {
            toast.error(response.data.message || 'Invalid invite code');
            navigate('/pricing');
            return;
          }
          setIsValid(true);
        } catch (error) {
          toast.error('Failed to validate invite code');
          navigate('/pricing');
          return;
        }
      } else {
        // Invalid access, redirect to pricing
        toast.error('Invalid registration link');
        navigate('/pricing');
        return;
      }

      setValidating(false);
    };

    validateAccess();
  }, [isFoundingMember, isInviteCode, inviteCode, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        email,
        password,
        display_name: displayName,
        is_founding_member: isFoundingMember,
        invite_code: isInviteCode ? inviteCode : null
      };

      const response = await axios.post(`${API}/promo/register-free`, payload);

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('display_name', response.data.display_name);
      localStorage.setItem('user_id', response.data.user_id);

      if (isFoundingMember) {
        toast.success(`Welcome to the Founding Members, ${displayName}! 🎉`);
      } else {
        toast.success(`Welcome to Radiant 40, ${displayName}!`);
      }
      
      setIsAuthenticated(true);
      navigate('/welcome');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Sparkles className="w-16 h-16 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-secondary-700 text-lg">Validating your access...</p>
        </div>
      </div>
    );
  }

  if (!isValid) {
    return null;
  }

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Button
          data-testid="back-button"
          onClick={() => navigate('/pricing')}
          variant="ghost"
          className="mb-4 text-secondary-900"
        >
          <ArrowLeft className="mr-2 w-5 h-5" /> Back to Pricing
        </Button>

        <div className="bg-white rounded-3xl shadow-2xl p-10">
          {/* Promo Banner */}
          {isFoundingMember && (
            <div className="bg-gradient-to-r from-amber-50 to-rose-50 border-2 border-amber-200 rounded-2xl p-4 mb-8 text-center">
              <div className="flex items-center justify-center gap-2 text-amber-700">
                <Gift className="w-6 h-6" />
                <span className="font-semibold">Founding Member Access</span>
              </div>
              <p className="text-sm text-amber-600 mt-1">You're getting the full 12-week course FREE!</p>
            </div>
          )}

          {isInviteCode && (
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-2xl p-4 mb-8 text-center">
              <div className="flex items-center justify-center gap-2 text-purple-700">
                <Ticket className="w-6 h-6" />
                <span className="font-semibold">Invite Code: {inviteCode}</span>
              </div>
              <p className="text-sm text-purple-600 mt-1">You've been invited for free access!</p>
            </div>
          )}

          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-4">
              <Flower2 className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-4xl font-serif font-bold text-secondary-900 mb-2">
              Create Your Account
            </h2>
            <p className="text-secondary-600">
              Complete your registration to begin
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-free-form">
            <div>
              <Label htmlFor="displayName" className="text-secondary-900 font-medium mb-2 block">
                Your Name
              </Label>
              <Input
                id="displayName"
                data-testid="display-name-input"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="What should we call you?"
                required
                className="w-full px-4 py-6 rounded-xl border-2 border-primary/20 focus:border-primary transition-colors"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-secondary-900 font-medium mb-2 block">
                Email Address
              </Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full px-4 py-6 rounded-xl border-2 border-primary/20 focus:border-primary transition-colors"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-secondary-900 font-medium mb-2 block">
                Password
              </Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password (min 6 characters)"
                required
                minLength={6}
                className="w-full px-4 py-6 rounded-xl border-2 border-primary/20 focus:border-primary transition-colors"
              />
            </div>

            <Button
              data-testid="register-free-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {loading ? 'Creating Account...' : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  {isFoundingMember ? 'Join as Founding Member' : 'Start My Free Journey'}
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-secondary-600 mb-2">Already have an account?</p>
            <Button
              data-testid="login-link-button"
              onClick={() => navigate('/auth')}
              variant="ghost"
              className="text-primary hover:text-primary/80 font-semibold"
            >
              Sign In Instead
            </Button>
          </div>

          <p className="text-center text-sm text-secondary-500 mt-4">
            By creating an account, you agree to our{' '}
            <button 
              type="button"
              onClick={() => navigate('/terms')}
              className="text-primary hover:underline"
            >
              Terms of Service
            </button>
            {' '}and{' '}
            <button 
              type="button"
              onClick={() => navigate('/privacy')}
              className="text-primary hover:underline"
            >
              Privacy Policy
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default RegisterFreePage;
